import React, { useEffect, useRef } from "react";
import L from "leaflet";

const goldIcon = (label) => L.divIcon({
  className: "",
  html: `<div style="background:#D4AF37;color:#050505;font-family:'Outfit',sans-serif;font-weight:700;font-size:11px;padding:6px 9px;border-radius:50%;width:26px;height:26px;display:flex;align-items:center;justify-content:center;box-shadow:0 4px 12px rgba(212,175,55,0.5);border:2px solid #050505;">${label}</div>`,
  iconSize: [26, 26],
  iconAnchor: [13, 13],
});

const driverIcon = () => L.divIcon({
  className: "",
  html: `<div style="position:relative;width:38px;height:38px;">
    <div style="position:absolute;inset:0;border-radius:50%;background:rgba(16,185,129,0.25);animation:dlx-pulse 2s ease-out infinite;"></div>
    <div style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);background:#10B981;width:18px;height:18px;border-radius:50%;border:3px solid #050505;box-shadow:0 0 12px rgba(16,185,129,0.8);"></div>
  </div>
  <style>@keyframes dlx-pulse{0%{transform:scale(0.6);opacity:0.8}100%{transform:scale(2);opacity:0}}</style>`,
  iconSize: [38, 38],
  iconAnchor: [19, 19],
});

export default function TrackMap({ pickup, dropoff, driver, height = 360 }) {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      const m = mapRef.current;
      mapRef.current = null;
      if (m) {
        try { m.off(); m.remove(); } catch (_e) { /* ignore */ }
      }
    };
  }, []);

  useEffect(() => {
    if (!ref.current || !aliveRef.current) return;

    if (!mapRef.current) {
      try {
        mapRef.current = L.map(ref.current, { zoomControl: true, attributionControl: false }).setView([43.0389, -87.9065], 10);
        L.tileLayer("https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png", { maxZoom: 19 }).addTo(mapRef.current);
      } catch (_e) { return; }
    }
    const m = mapRef.current;
    if (!m) return;

    try {
      m.eachLayer(layer => {
        if (layer instanceof L.Marker || layer instanceof L.Polyline) m.removeLayer(layer);
      });
    } catch (_e) { return; }

    const bounds = [];
    try {
      if (pickup) {
        L.marker([pickup.lat, pickup.lng], { icon: goldIcon("A") }).addTo(m).bindTooltip("Pickup");
        bounds.push([pickup.lat, pickup.lng]);
      }
      if (dropoff) {
        L.marker([dropoff.lat, dropoff.lng], { icon: goldIcon("B") }).addTo(m).bindTooltip("Drop-off");
        bounds.push([dropoff.lat, dropoff.lng]);
      }
      if (pickup && dropoff) {
        L.polyline([[pickup.lat, pickup.lng], [dropoff.lat, dropoff.lng]], {
          color: "#D4AF37", weight: 2, opacity: 0.7, dashArray: "8 8",
        }).addTo(m);
      }
      if (driver) {
        L.marker([driver.lat, driver.lng], { icon: driverIcon() }).addTo(m).bindTooltip("Your chauffeur");
        bounds.push([driver.lat, driver.lng]);
      }
      if (bounds.length > 1) m.fitBounds(bounds, { padding: [50, 50], maxZoom: 14 });
      else if (bounds.length === 1) m.setView(bounds[0], 13);
    } catch (_e) { /* unmounted mid-flight */ }
  }, [pickup, dropoff, driver]);

  return (
    <div
      data-testid="track-map"
      ref={ref}
      style={{ height: `${height}px`, width: "100%", background: "#F5F2EB", border: "1px solid #E5E1D8" }}
    />
  );
}
